import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet, UploadCloud, Eye, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { SOURCES, SOURCE_LABELS, errMsg, fmtMoney, fmtPeriod, useApi } from "@/lib/api";
import { TrialBalanceUpload } from "@/components/TrialBalanceUpload";

const srcLabel = (s) => SOURCE_LABELS[s] || s;

const ReadOnlyNote = () => (
  <div className="panel p-8 max-w-3xl" data-testid="upload-locked">
    <div className="font-serif text-2xl">Uploads are paused</div>
    <p className="text-slate-600 mt-2 text-sm">Your firm's subscription isn't active, so statements are view-only. An admin can subscribe on the Billing page.</p>
    <Link to="/billing" className="inline-block mt-5 bg-slate-900 text-white px-5 py-2.5 text-sm hover:bg-slate-800 transition-colors duration-200" data-testid="upload-locked-billing">Go to billing</Link>
  </div>
);

function UploadForm({ onDone }) {
  const api = useApi();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [source, setSource] = useState("");
  const [period, setPeriod] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const pick = async (f) => {
    if (!f) return;
    setFile(f);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/uploads/inspect", fd);
      if (r.data.detected_source) setSource(r.data.detected_source);
      if (r.data.detected_period) setPeriod(r.data.detected_period);
      toast.message(`Detected ${srcLabel(r.data.detected_source)} · ${r.data.row_count} rows`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("source", source);
    fd.append("period", period);
    setBusy(true);
    try {
      const r = await api.post("/uploads", fd);
      toast.success(`Imported ${r.data.row_count} rows for ${fmtPeriod(r.data.period)}${r.data.replaced ? " (replaced earlier upload)" : ""}`);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onDone(r.data.period);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel p-6 sm:p-8 grid lg:grid-cols-[1.3fr_1fr] gap-8">
      <label
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
        className={`border-2 border-dashed flex flex-col items-center justify-center text-center p-10 cursor-pointer transition-colors duration-200 ${drag ? "border-amber-600 bg-amber-50" : "border-slate-300 hover:border-slate-500"}`}
        data-testid="upload-dropzone"
      >
        <input ref={inputRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="upload-file-input" />
        {file ? <FileSpreadsheet className="h-10 w-10 text-emerald-700" strokeWidth={1.25} /> : <UploadCloud className="h-10 w-10 text-slate-400" strokeWidth={1.25} />}
        <div className="mt-4 font-medium text-slate-900" data-testid="upload-file-name">{file ? file.name : "Drop an Excel file or click to browse"}</div>
        <div className="text-xs text-slate-500 mt-1">AccountingMASTER, BrokerageMASTER, InputInvestmentsMASTER, LandMASTER, OilMASTER</div>
      </label>
      <div className="space-y-5">
        <div>
          <Label className="small-label">Source</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="mt-2 rounded-sm" data-testid="upload-source-select"><SelectValue placeholder="Choose source" /></SelectTrigger>
            <SelectContent>
              {SOURCES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`upload-source-${s.value}`}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="small-label">Month end</Label>
          <Input type="date" value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-2 rounded-sm font-mono" data-testid="upload-month-end-input" />
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          Uploads are current balances for the month end. Uploading the same source again for the same month end replaces it; other months stay in history.
        </p>
        <Button onClick={submit} disabled={!file || !source || !period || busy} className="w-full rounded-sm h-11" data-testid="upload-submit-button">
          {busy ? "Importing…" : "Import spreadsheet"}
        </Button>
      </div>
    </div>
  );
}

function LinesDialog({ uploadId, onClose }) {
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (uploadId) api.get(`/uploads/${uploadId}/lines`).then((r) => setD(r.data));
  }, [api, uploadId]);
  return (
    <Dialog open={!!uploadId} onOpenChange={(o) => !o && (onClose(), setD(null))}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="upload-lines-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{d?.upload.filename || "Loading…"}</DialogTitle></DialogHeader>
        <table className="w-full text-sm">
          <thead><tr className="text-left small-label border-b"><th className="py-2">Entity</th><th>Category / item</th><th className="text-right">Amount</th></tr></thead>
          <tbody>
            {(d?.lines || []).map((l, k) => (
              <tr key={k} className="border-b border-slate-100">
                <td className="py-1.5 pr-3">{l.entity_name}</td>
                <td className="pr-3 text-slate-600">{l.kind === "calc" ? <span>→ {l.target_name} <span className="t-mark">t</span></span> : l.kind === "invest" ? l.label : l.category}</td>
                <td className="num text-right">{l.kind === "calc" ? "calculated" : fmtMoney(l.kind === "liability" ? -l.amount : l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}

function UploadTabs({ onDone }) {
  const api = useApi();
  const [tab, setTab] = useState("sources");
  const { periods } = useApp();
  const [ents, setEnts] = useState([]);
  useEffect(() => {
    if (tab !== "sources") api.get("/entities").then((r) => setEnts([...r.data.entities].sort((a, b) => a.name.localeCompare(b.name))));
  }, [api, tab]);
  return (
    <div>
      <div className="flex gap-1 border-b border-slate-200 mb-6" role="tablist">
        {[["sources", "Data sources"], ["tb", "Trial balance"], ["quicken", "Quicken"]].map(([k, l]) => (
          <button key={k} role="tab" onClick={() => setTab(k)} data-testid={`upload-tab-${k}`}
            className={`px-4 py-2.5 text-sm -mb-px border-b-2 transition-colors duration-200 ${tab === k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>
      {tab === "sources" ? <UploadForm onDone={onDone} /> : <TrialBalanceUpload key={tab} format={tab === "quicken" ? "quicken" : "trialbalance"} entities={ents} periods={periods || []} onDone={onDone} />}
    </div>
  );
}

export default function Uploads() {
  const api = useApi();
  const { loadPeriods, setPeriod, me } = useApp();
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  const load = useCallback(() => api.get("/uploads").then((r) => setRows(r.data)), [api]);

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.filename} for ${u.period}? Its balances are removed from that month end.`)) return;
    try {
      await api.delete(`/uploads/${u.id}`);
      toast.success("Upload deleted");
      await load();
      await loadPeriods();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const done = async (p) => {
    await load();
    await loadPeriods();
    setPeriod(p);
  };

  return (
    <div className="max-w-[1400px]">
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">Uploads</h1>
      <p className="text-slate-500 mt-3">Import month-end spreadsheets. Each upload is tagged with its month end and kept for history.</p>
      <div className="mt-10">{me?.access?.can_edit ? <UploadTabs onDone={done} /> : <ReadOnlyNote />}</div>
      <div className="panel mt-10 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="upload-history-table">
          <thead className="bg-slate-100">
            <tr className="text-left small-label"><th className="px-5 py-3">Month end</th><th>Source</th><th>File</th><th>Rows</th><th>Uploaded</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors duration-200" data-testid={`upload-row-${u.id}`}>
                <td className="px-5 py-3 num">{u.period}</td>
                <td>{srcLabel(u.source)}{u.entity_name && <div className="text-xs text-slate-500">{u.entity_name}</div>}</td>
                <td className="font-mono text-xs text-slate-600">{u.filename}</td>
                <td className="num">{u.row_count}</td>
                <td className="text-xs text-slate-500">{new Date(u.uploaded_at).toLocaleString()}<div>{u.uploaded_by}</div></td>
                <td>{u.superseded ? <span className="text-xs text-slate-400">Replaced</span> : <span className="ok-badge">Active</span>}</td>
                <td className="pr-4 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setView(u.id)} data-testid={`upload-view-${u.id}`}><Eye className="h-4 w-4" /></Button>
                  {me?.role !== "member" && me?.access?.can_edit && (
                    <Button variant="ghost" size="sm" onClick={() => remove(u)} data-testid={`upload-delete-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LinesDialog uploadId={view} onClose={() => setView(null)} />
    </div>
  );
}
